<template>
  <Transition name="subtitle-notification">
    <div
      v-if="visible"
      ref="rootRef"
      class="subtitle-notification"
      :class="{ 'subtitle-notification--persistent': persistent }"
      :role="persistent ? 'group' : 'status'"
      :aria-live="persistent ? 'off' : 'polite'"
      :aria-labelledby="persistent ? messageId : undefined"
      :tabindex="persistent ? -1 : undefined"
      @keydown.esc.stop.prevent="dismiss"
    >
      <span :id="messageId" class="subtitle-notification__text">{{ message }}</span>
      <button
        class="subtitle-notification__settings"
        @click="onOpenSettings"
      >
        {{ t('player.panels.subtitles.openSettings') }}
      </button>
      <button
        v-if="persistent"
        class="subtitle-notification__close"
        :aria-label="t('player.panels.subtitles.dismissNotification')"
        @click="dismiss"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, useId } from 'vue'
import { useVideoI18n } from '../../i18n'
import { useInputModality } from '../../../common/composables/useInputModality'

const AUTO_HIDE_DELAY = 3000

const emit = defineEmits<{
  openSettings: []
}>()

const { t } = useVideoI18n()
const { isKeyboardModality } = useInputModality()

const messageId = useId()
const rootRef = ref<HTMLElement | null>(null)
const visible = ref(false)
const message = ref('')

/**
 * Persistent mode: the notification stays until dismissed, takes focus and
 * offers a close button. Enabled when the subtitles were toggled with the
 * keyboard, the closest assistive-technology signal a browser gives us.
 */
const persistent = ref(false)

let hideTimer: ReturnType<typeof setTimeout> | null = null
let previouslyFocused: HTMLElement | null = null

function clearHideTimer(): void {
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }
}

interface ShowOptions {
  /** Overrides the keyboard-modality heuristic. */
  persistent?: boolean
}

/**
 * Displays the notification.
 *
 * @returns True when the notification took focus, meaning it announces itself
 *          and the caller must not also push the message to the live region.
 */
function show(trackName: string, options: ShowOptions = {}): boolean {
  message.value = t('player.panels.subtitles.enabledNotification', { track: trackName })
  persistent.value = options.persistent ?? isKeyboardModality.value
  visible.value = true
  clearHideTimer()

  if (!persistent.value) {
    hideTimer = setTimeout(() => {
      visible.value = false
    }, AUTO_HIDE_DELAY)
    return false
  }

  // Re-showing while already focused must not make the notification its own
  // focus-return target.
  const active = document.activeElement as HTMLElement | null
  if (!rootRef.value?.contains(active)) previouslyFocused = active

  nextTick(() => rootRef.value?.focus())
  return true
}

/**
 * Hides the notification. When focus is inside it, focus goes back to where it
 * was before the notification claimed it, so it never falls to <body> when the
 * node is removed; focus that already moved elsewhere is left alone.
 */
function hide(): void {
  clearHideTimer()
  const hadFocus = rootRef.value?.contains(document.activeElement) ?? false
  const target = previouslyFocused
  previouslyFocused = null
  visible.value = false
  if (hadFocus && target?.isConnected) target.focus()
}

function dismiss(): void {
  hide()
}

function onOpenSettings(): void {
  // Hand focus back before the node is removed, then let the panel claim it.
  hide()
  emit('openSettings')
}

onBeforeUnmount(clearHideTimer)

defineExpose({ show, hide })
</script>

<style scoped lang="scss">
.subtitle-notification {
  position: absolute;
  top: 3.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 60;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background: var(--vp-backdrop-medium);
  backdrop-filter: blur(8px);
  border-radius: 8px;
  pointer-events: auto;
  max-width: calc(100% - 2rem);
  color: var(--vp-overlay-text-color, #ffffff);

  &:focus-visible {
    outline: 2px solid var(--vp-primary-color, #007bff);
    outline-offset: 2px;
  }

  &__text {
    font-size: 0.8125rem;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  &__settings {
    font-size: 0.75rem;
    font-weight: 600;
    color: inherit;
    background: var(--vp-surface-elevated);
    border: none;
    cursor: pointer;
    padding: 4px 10px;
    border-radius: 4px;
    font-family: inherit;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: var(--vp-surface-hover);
    }

    &:focus-visible {
      outline: 2px solid var(--vp-primary-color, #007bff);
      outline-offset: 2px;
    }
  }

  &__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    padding: 0;
    color: inherit;
    background: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.15s ease;

    svg {
      width: 16px;
      height: 16px;
    }

    &:hover {
      background-color: var(--vp-surface-hover);
    }

    &:focus-visible {
      outline: 2px solid var(--vp-primary-color, #007bff);
      outline-offset: 2px;
    }
  }
}

.subtitle-notification-enter-active {
  animation: notification-in 200ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.subtitle-notification-leave-active {
  animation: notification-out 200ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

@keyframes notification-in {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

@keyframes notification-out {
  from {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
  to {
    opacity: 0;
    transform: translateX(-50%) translateY(-8px);
  }
}

@media (max-width: 768px) {
  .subtitle-notification {
    top: 2.75rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .subtitle-notification-enter-active,
  .subtitle-notification-leave-active {
    animation-duration: 1ms;
  }
}
</style>
